import { CaretDownOutlined, CaretUpOutlined, DragOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import {
  type Key,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { usePriorityMove } from '../PriorityMoveContext';

import { DragHandle, DragPriorityButtonSlot, DragPriorityCell } from './DragPriorityControls.style';

export type DragPriorityControlsProps = {
  itemKey: Key;
};

const stopRowDrag = (event: ReactMouseEvent | ReactPointerEvent) => {
  event.stopPropagation();
};

const findVisibleSibling = (
  cell: HTMLDivElement | null,
  direction: 'up' | 'down'
): HTMLTableRowElement | undefined => {
  const row = cell?.closest('tr.ant-table-row');
  let sibling = direction === 'up' ? row?.previousElementSibling : row?.nextElementSibling;

  // Expanded rows and other structural rows may sit between two data rows.
  while (sibling) {
    if (sibling.matches('tr.ant-table-row[data-row-key]')) {
      return sibling as HTMLTableRowElement;
    }
    sibling = direction === 'up' ? sibling.previousElementSibling : sibling.nextElementSibling;
  }

  return undefined;
};

export const DragPriorityControls = ({ itemKey }: DragPriorityControlsProps) => {
  const { t } = useTranslation();
  const movePriorityItem = usePriorityMove();
  const cellRef = useRef<HTMLDivElement | null>(null);
  const [availableMoves, setAvailableMoves] = useState({ up: false, down: false });

  useLayoutEffect(() => {
    const updateAvailableMoves = () => {
      const nextMoves = {
        up: Boolean(findVisibleSibling(cellRef.current, 'up')),
        down: Boolean(findVisibleSibling(cellRef.current, 'down')),
      };
      setAvailableMoves(previous =>
        previous.up === nextMoves.up && previous.down === nextMoves.down ? previous : nextMoves
      );
    };

    updateAvailableMoves();
    const tableBody = cellRef.current?.closest('tbody');
    if (!tableBody) {
      return undefined;
    }

    const observer = new MutationObserver(updateAvailableMoves);
    observer.observe(tableBody, { childList: true });
    return () => observer.disconnect();
  }, [itemKey]);

  const handleMove = (direction: 'up' | 'down') => (event: ReactMouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const targetKey = findVisibleSibling(cellRef.current, direction)?.dataset.rowKey;
    if (targetKey !== undefined) {
      movePriorityItem?.(itemKey, targetKey);
    }
  };

  return (
    <DragPriorityCell ref={cellRef}>
      <DragHandle>
        <DragOutlined />
      </DragHandle>
      <DragPriorityButtonSlot>
        {availableMoves.up ? (
          <Button
            type="text"
            size="small"
            data-testid="priority-move-up"
            title={t('table.movePriorityUp', 'Move Up')}
            aria-label={t('table.movePriorityUp', 'Move Up')}
            icon={<CaretUpOutlined />}
            onMouseDown={stopRowDrag}
            onPointerDown={stopRowDrag}
            onClick={handleMove('up')}
          />
        ) : null}
      </DragPriorityButtonSlot>
      <DragPriorityButtonSlot>
        {availableMoves.down ? (
          <Button
            type="text"
            size="small"
            data-testid="priority-move-down"
            title={t('table.movePriorityDown', 'Move Down')}
            aria-label={t('table.movePriorityDown', 'Move Down')}
            icon={<CaretDownOutlined />}
            onMouseDown={stopRowDrag}
            onPointerDown={stopRowDrag}
            onClick={handleMove('down')}
          />
        ) : null}
      </DragPriorityButtonSlot>
    </DragPriorityCell>
  );
};
